// Bibliothèque de logos (Daniel 09.10 / feu vert 15.10.2026) — client des endpoints /api/library/* de la blackbox.
// Règles : jamais un fichier de la boutique ne redescend dans le navigateur (seulement des aperçus ≤ 1200 px signés 5 min) ;
// les octets montent DIRECTEMENT dans le bucket privé de staging via une URL d'envoi signée, puis le serveur relit, vérifie et commit.
import { refreshSiteSession, siteAccessToken } from './exportPlanche';
import { DTFItem } from './packing';

export class LibraryAuthError extends Error {}
export class LibraryError extends Error {
	constructor(message: string, public code?: string, public status?: number) {
		super(message);
	}
}

export interface LibraryLogo {
	id: string;
	name: string;
	created_at: string;
	version_id: string | null;
	kind: string | null;
	media_type: string | null;
	width: number | null;
	height: number | null;
	steps: string[];
	vectorized: boolean;
	thumb_url: string | null;
}
export interface LibraryPreview {
	logo_id: string | null;
	name: string | null;
	version_id: string | null;
	blob_id: string;
	kind: string | null;
	media_type: string;
	width: number | null;
	height: number | null;
	preview_url: string | null;
	preview_width: number | null;
	preview_height: number | null;
	thumb_url: string | null;
	vectorized: boolean;
	/** par fichier (planche recommandée) : 'texte' = élément sans version de logo (numéros/texte), sinon 'logo' */
	item_kind?: 'logo' | 'texte' | null;
}
export interface LibraryBoardItem {
	id: string;
	blob_id: string;
	logo_version_id: string | null;
	item_kind: 'logo' | 'texte';
	label: string;
	x_mm: number;
	y_mm: number;
	rotation_deg: number;
	width_mm: number;
	height_mm: number;
	item_width_mm: number;
	item_height_mm: number;
	logo_id: string | null;
	logo_name: string | null;
	logo_active: boolean | null;
}
export interface LibraryBoard {
	board_version_id: string;
	board_id: string;
	/** Nom donné dans l'éditeur (« Nom de la planche ») — vide si aucun */
	title: string;
	revision: number;
	width_mm: number;
	height_mm: number;
	qty: number;
	glue: 'base' | 'anti';
	source: string;
	created_at: string;
	preview_url: string | null;
	items: { label: string; kind: string; width_mm: number; height_mm: number }[];
	order: { order_id: string; at: string; price: Record<string, unknown>; order_created_at?: string | null; total_ht?: number | null; traite?: boolean | null } | null;
}
export interface LibraryBoardDetail extends Omit<LibraryBoard, 'items'> {
	items: LibraryBoardItem[];
}

/** Connecté sur le site (jeton Supabase dans localStorage, posé par header.js) ? */
export const isLoggedIn = (): boolean => !!siteAccessToken();

/** URL Supabase du site, déduite de la clé localStorage sb-<ref>-auth-token (même projet que le serveur). */
function siteSupabaseUrl(): string {
	try {
		for (let i = 0; i < localStorage.length; i++) {
			const m = /^sb-([a-z0-9]+)-auth-token$/.exec(localStorage.key(i) || '');
			if (m) return `https://${m[1]}.supabase.co`;
		}
	} catch {
		/* ignore */
	}
	return '';
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
	const t = await refreshSiteSession();
	if (!t) throw new LibraryAuthError('non connecté');
	const r = await fetch(`${window.location.origin}/api/library/${path}`, {
		...init,
		headers: { ...(init.headers || {}), Authorization: `Bearer ${t}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
		cache: 'no-store',
	});
	const j = (await r.json().catch(() => ({}))) as { error?: string; code?: string } & T;
	if (r.status === 401) throw new LibraryAuthError(j.error || 'session expirée');
	if (!r.ok) throw new LibraryError(j.error || `HTTP ${r.status}`, j.code, r.status);
	return j;
}

export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
	const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
	const d = await crypto.subtle.digest('SHA-256', buf as BufferSource);
	return Array.from(new Uint8Array(d)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function dataUrlToBlob(dataUrl: string): Blob {
	const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl);
	if (!m) throw new LibraryError('fichier illisible');
	const type = m[1] || 'application/octet-stream';
	if (m[2]) {
		const bin = atob(m[3]);
		const u8 = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
		return new Blob([u8], { type });
	}
	return new Blob([decodeURIComponent(m[3])], { type });
}

/** Envoi d'un fichier : init (opération) → PUT direct dans le staging privé → commit serveur (relecture, SHA-256, clé définitive). */
export async function uploadBytes(blob: Blob, purpose: 'logo_original' | 'logo_processed', origin: 'client_upload' | 'editeur', kind: 'original' | 'prepared', idemKey: string): Promise<{ blob_id: string; sha256: string; width: number | null; height: number | null; media_type: string }> {
	const type = blob.type === 'image/svg+xml' || blob.type === 'image/png' || blob.type === 'image/jpeg' || blob.type === 'image/webp' || blob.type === 'application/pdf' ? blob.type : 'application/octet-stream';
	if (type === 'application/octet-stream') throw new LibraryError('Type de fichier non accepté');
	const init = await api<{ op_id: string; bucket?: string; path?: string; token?: string; already?: { blob_id: string } }>('upload-init', {
		method: 'POST',
		body: JSON.stringify({ purpose, media_type: type, byte_size: blob.size, idempotency_key: idemKey }),
	});
	if (init.already?.blob_id) {
		const c = await api<{ blob_id: string; sha256: string; width: number | null; height: number | null; media_type: string }>('upload-commit', { method: 'POST', body: JSON.stringify({ op_id: init.op_id, origin, kind }) });
		return c;
	}
	const base = siteSupabaseUrl();
	if (!base || !init.path || !init.token) throw new LibraryError('envoi impossible (configuration)');
	const put = await fetch(`${base}/storage/v1/object/upload/sign/${init.bucket}/${init.path.split('/').map(encodeURIComponent).join('/')}?token=${encodeURIComponent(init.token)}`, {
		method: 'PUT',
		headers: { 'Content-Type': type, 'x-upsert': 'false' },
		body: blob,
	});
	if (!put.ok && put.status !== 409) throw new LibraryError(`envoi du fichier refusé (HTTP ${put.status})`);
	return api('upload-commit', { method: 'POST', body: JSON.stringify({ op_id: init.op_id, origin, kind }) });
}

/** Octets FINIS d'un élément (ce qui s'imprime) : SVG d'origine si vectoriel, sinon l'aperçu rendu (PNG). */
export function finalBlobOf(item: DTFItem): Blob {
	if (item.kind === 'svg' && item.svgText) return new Blob([item.svgText], { type: 'image/svg+xml' });
	return dataUrlToBlob(item.src);
}

/** Octets d'ORIGINE (fichier importé tel quel) s'ils existent encore. */
export function originalBlobOf(item: DTFItem): Blob | null {
	if (item.importOriginal) return dataUrlToBlob(item.importOriginal);
	return null;
}

/** Garantit que le fichier fini (et l'original) de l'élément sont dans la boutique ; renvoie les identifiants (mis en cache sur l'élément). */
export async function ensureItemBlobs(item: DTFItem, onProgress?: (msg: string) => void): Promise<{ finalBlobId: string; finalSha: string; originalBlobId: string | null; width: number | null; height: number | null }> {
	if (item.library?.blobId) return { finalBlobId: item.library.blobId, finalSha: item.finalBlobSha || '', originalBlobId: null, width: item.library.width, height: item.library.height };
	const fin = finalBlobOf(item);
	const sha = await sha256Hex(await fin.arrayBuffer());
	if (item.finalBlobId && item.finalBlobSha === sha) return { finalBlobId: item.finalBlobId, finalSha: sha, originalBlobId: item.originalBlobId || null, width: item.finalWidth ?? null, height: item.finalHeight ?? null };
	onProgress?.(item.name);
	const steps = stepsOf(item);
	const processed = steps.length > 0 || !item.importOriginal;
	const up = await uploadBytes(fin, processed ? 'logo_processed' : 'logo_original', processed ? 'editeur' : 'client_upload', processed ? 'prepared' : 'original', `fin-${sha}`);
	let originalBlobId: string | null = null;
	const orig = originalBlobOf(item);
	if (orig && steps.length > 0) {
		try {
			const osha = await sha256Hex(await orig.arrayBuffer());
			if (osha !== sha) originalBlobId = (await uploadBytes(orig, 'logo_original', 'client_upload', 'original', `orig-${osha}`)).blob_id;
			else originalBlobId = up.blob_id;
		} catch {
			originalBlobId = null; // l'original est un confort (traçabilité) : son échec ne bloque pas la commande
		}
	} else if (orig) originalBlobId = up.blob_id;
	return { finalBlobId: up.blob_id, finalSha: sha, originalBlobId, width: up.width, height: up.height };
}

/** Traitements faits dans l'éditeur sur cet élément, dans l'ordre réel du parcours (provenance : fiche du logo + processing_job_steps). */
export interface StepInfo {
	step: 'rendu' | 'fond' | 'nettete' | 'vecto' | 'traits' | 'couleur' | 'lissage';
	engine: string;
	provider: string;
	model: string;
	model_version: string;
	params?: Record<string, unknown>;
}
export function stepsDetail(item: DTFItem): StepInfo[] {
	const s: StepInfo[] = [];
	if (item.kind === 'pdf' || (item.importOriginal && /^data:application\/pdf/i.test(item.importOriginal) && item.kind !== 'svg')) s.push({ step: 'rendu', engine: 'navigateur', provider: 'editeur-v2', model: 'pdfjs', model_version: '1', params: { maxPx: 2000 } });
	if (item.bgRemoved) s.push({ step: 'fond', engine: 'navigateur', provider: 'editeur-v2', model: 'bgremove', model_version: '1' });
	if (item.improved) s.push({ step: 'nettete', engine: 'wavespeed', provider: 'wavespeed', model: 'upscale-dtf', model_version: '1' });
	if (item.vectorized) s.push({ step: 'vecto', engine: 'vectorizer.ai', provider: 'vectorizer.ai', model: 'api', model_version: 'v1' });
	if (item.thinFixed) s.push({ step: 'traits', engine: 'navigateur', provider: 'editeur-v2', model: 'thinfix', model_version: '1' });
	if (item.smoothed) s.push({ step: 'lissage', engine: 'navigateur', provider: 'editeur-v2', model: 'smooth', model_version: '1' });
	if (item.monoColor) s.push({ step: 'couleur', engine: 'navigateur', provider: 'editeur-v2', model: 'recolor', model_version: '1', params: { color: item.monoColor } });
	return s;
}
export const stepsOf = (item: DTFItem): string[] => stepsDetail(item).map(x => x.step);

export async function saveLogoToLibrary(item: DTFItem, name: string, keep: boolean): Promise<{ logo_id: string; version_id: string; thumb_url: string | null; name: string | null; finalBlobId: string; finalSha: string; originalBlobId: string | null; width: number | null; height: number | null }> {
	const b = await ensureItemBlobs(item);
	const steps = stepsDetail(item);
	const body: Record<string, unknown> = { name, keep, original_blob_id: b.originalBlobId, final_blob_id: b.finalBlobId, steps, providers: { vecto: item.vectorized ? 'vectorizer.ai' : undefined, nettete: item.improved ? 'wavespeed' : undefined }, idempotency_key: `save-${b.finalSha.slice(0, 48)}-${keep ? 'k' : 'a'}` };
	const r = await api<{ logo_id: string; version_id: string; thumb_url: string | null; name: string | null }>('logo-save', { method: 'POST', body: JSON.stringify(body) });
	return { ...r, finalBlobId: b.finalBlobId, finalSha: b.finalSha, originalBlobId: b.originalBlobId, width: b.width, height: b.height };
}

export const listLogos = () => api<{ logos: LibraryLogo[] }>('logos').then(r => r.logos);
export const renameLogo = (logo_id: string, name: string) => api<{ ok: true }>('logos', { method: 'POST', body: JSON.stringify({ action: 'rename', logo_id, name }) });
export const archiveLogo = (logo_id: string) => api<{ ok: true }>('logos', { method: 'POST', body: JSON.stringify({ action: 'archive', logo_id }) });
/** « Couleur unique » sur un logo de la bibliothèque (Daniel 26.10) : le serveur recolore le VRAI fichier et rend une version archivée
 * (le logo de la bibliothèque reste tel quel) ; c'est cette version que la planche cite. Même logo + même couleur = même version. */
export const recolorLibraryLogo = (version_id: string, color: string) =>
	api<{ version_id: string; blob_id: string; width: number | null; height: number | null; media_type: string | null }>('logo-recolor', { method: 'POST', body: JSON.stringify({ version_id, color }) });
export const logoPreview = (ref: { version?: string; blob?: string }) => api<LibraryPreview>(`logo-preview?${ref.version ? `version=${encodeURIComponent(ref.version)}` : `blob=${encodeURIComponent(ref.blob || '')}`}`);
export const listBoards = () => api<{ boards: LibraryBoard[] }>('boards').then(r => r.boards);
export const boardDetail = (version: string) => api<LibraryBoardDetail>(`boards?version=${encodeURIComponent(version)}`);

export interface ComposePlanche {
	width_cm: number;
	height_cm: number;
	qty: number;
	glue: 'base' | 'anti';
	items: { blob_id: string; logo_version_id: string | null; item_kind: 'logo' | 'texte'; label: string; x_mm: number; y_mm: number; width_mm: number; height_mm: number; item_width_mm: number; item_height_mm: number; rotation_deg: number }[];
}
export interface ComposedBoard {
	board_id: string;
	board_version_id: string;
	revision: number;
	replay: boolean;
	width_mm: number;
	height_mm: number;
	qty: number;
	glue: 'base' | 'anti';
	items: number;
	preview_url: string | null;
}
export const composeBoards = (planches: ComposePlanche[], source: 'editeur-v2' | 'reorder' | 'admin' = 'editeur-v2', title?: string) => api<{ boards: ComposedBoard[] }>('board-compose', { method: 'POST', body: JSON.stringify({ planches, source, title: title || '' }) }).then(r => r.boards);
/** ADMIN : URL signée (2 min, journalisée) du fichier d'impression composé côté serveur. 403 pour un client. */
export const adminPrintUrl = (version: string) => api<{ url: string; filename: string; bytes: number }>(`admin-print?version=${encodeURIComponent(version)}`);

/** Charge une URL signée en data URL (pour persister l'aperçu localement : l'URL expire en 5 min). */
export async function fetchToDataUrl(url: string): Promise<string> {
	const r = await fetch(url, { cache: 'no-store' });
	if (!r.ok) throw new LibraryError('aperçu indisponible');
	const b = await r.blob();
	return new Promise((resolve, reject) => {
		const fr = new FileReader();
		fr.onload = () => resolve(String(fr.result));
		fr.onerror = () => reject(fr.error);
		fr.readAsDataURL(b);
	});
}

/** Petite vignette (≤ 320 px) à partir d'une data URL, pour le panier. */
export async function smallThumb(dataUrl: string, max = 320): Promise<string> {
	const im = new Image();
	await new Promise<void>((res, rej) => {
		im.onload = () => res();
		im.onerror = () => rej(new Error('vignette'));
		im.src = dataUrl;
	});
	const s = Math.min(1, max / Math.max(im.naturalWidth, im.naturalHeight));
	const c = document.createElement('canvas');
	c.width = Math.max(1, Math.round(im.naturalWidth * s));
	c.height = Math.max(1, Math.round(im.naturalHeight * s));
	c.getContext('2d')?.drawImage(im, 0, 0, c.width, c.height);
	return c.toDataURL('image/png');
}

/** Nom proposé pour un logo : « judo-lausanne_v2.png » → « Judo lausanne v2 ». */
/** Nom de fichier qui ne veut rien dire pour un humain (hash, UUID, identifiant d'export : « 8c758f456c2841dd9859a1723821f999 »,
 *  « IMG_4521 », « image (3) », « téléchargement ») → on ne l'affiche pas et on ne le propose pas comme nom de logo. */
export function isMeaninglessName(fileName: string): boolean {
	const base = fileName.replace(/\.[a-z0-9]{2,5}$/i, '').trim();
	if (!base) return true;
	if (/^[0-9a-f-]{16,}$/i.test(base)) return true; // hash hex, UUID
	if (/^[0-9a-z_-]{24,}$/i.test(base) && !/[aeiouy]{1}[a-z]/i.test(base.replace(/\d/g, ''))) return true; // identifiant long sans mot
	if (/^(img|image|dsc|dscn|pxl|photo|screenshot|capture|scan|untitled|sans[ _-]?titre|t[ée]l[ée]chargement|download)[ _-]?[\d() _-]*$/i.test(base)) return true;
	return false;
}

export function suggestName(fileName: string): string {
	if (isMeaninglessName(fileName)) return '';
	const base = fileName.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
	return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
}

// ── Polices du client gardées avec le compte (Daniel 21.10.2026) — /api/library/fonts
export interface AccountFont { id: string; display_name: string; sha256: string; byte_size: number; media_type: string; created_at: string }
export const listAccountFonts = () => api<{ fonts: AccountFont[] }>('fonts').then(r => r.fonts);
export const saveAccountFont = (name: string, bytes: ArrayBuffer) => {
	let bin = '';
	const u8 = new Uint8Array(bytes);
	for (let i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, Array.from(u8.subarray(i, i + 0x8000)));
	return api<{ font: AccountFont; already: boolean }>('fonts', { method: 'POST', body: JSON.stringify({ action: 'save', name, data_base64: btoa(bin) }) });
};
export const archiveAccountFont = (font_id: string) => api<{ ok: true }>('fonts', { method: 'POST', body: JSON.stringify({ action: 'archive', font_id }) });
export async function fetchAccountFontBytes(id: string): Promise<ArrayBuffer> {
	const t = await refreshSiteSession();
	if (!t) throw new LibraryAuthError('non connecté');
	const r = await fetch(`${window.location.origin}/api/library/fonts?id=${encodeURIComponent(id)}&file=1`, { headers: { Authorization: `Bearer ${t}` }, cache: 'no-store' });
	if (r.status === 401) throw new LibraryAuthError('session expirée');
	if (!r.ok) throw new LibraryError(`HTTP ${r.status}`, undefined, r.status);
	return r.arrayBuffer();
}
